import { useMemo } from "react";
import PropTypes from "prop-types";

import DifferentiationHierarchyPanel from "../studentDifferentiation/PanelUI/DifferentiationHierarchyPanel";

import { useReportableHierarchy } from "../../../utils/TemplateToReportableHierarchy";

import "../../../styles/StudentDiffForm.scss";
import "../../../styles/SetupDifferentiation.scss";

/**
 * Step 2: Select template items to exclude for the selected students
 */
const SetupExcludeItems = ({ selectedTemplate, excludedItemIds, setExcludedItemIds }) => {
    const { tree: currentTree } = useReportableHierarchy(selectedTemplate);

    const handleToggle = (node) => {
        const item = { templateContentId: node.templateContentId, contentType: node.contentType };
        setExcludedItemIds((prev) => {
            const exists = prev.find((i) => i.templateContentId === item.templateContentId);
            return exists ? prev.filter((i) => i.templateContentId !== item.templateContentId) : [...prev, item];
        });
    };

    const selectedKeys = useMemo(() => {
        const keys = new Set();

        const isItemExcluded = (item) => {
            return (
                item.templateContentId && excludedItemIds.some((e) => e.templateContentId === item.templateContentId)
            );
        };

        const addKeyIfExcluded = (item) => {
            if (isItemExcluded(item)) {
                const key = `${item.contentType}:${item.templateContentId}`;
                keys.add(key);
            }
        };

        const traverse = (items) => {
            items?.forEach((item) => {
                addKeyIfExcluded(item);
                if (item.children?.length > 0) {
                    traverse(item.children);
                }
            });
        };

        if (currentTree) traverse(currentTree);
        return keys;
    }, [currentTree, excludedItemIds]);

    return (
        <section>
            <h5 className="setup-step-title">Exclude Template Items</h5>
            <div className="setup-step-directions">
                Select content items from the grade-level template to exclude for the selected student(s). Content items
                that you elect to exclude for the student(s) will not appear on their final progress report as they will
                not be ratable.
            </div>
            <div className="current-content-items">
                <DifferentiationHierarchyPanel
                    dataType="CURRENT"
                    selectedTemplate={selectedTemplate}
                    tree={currentTree}
                    selectedKeys={selectedKeys}
                    selectedCount={excludedItemIds.length}
                    onToggle={handleToggle}
                />
            </div>
        </section>
    );
};

SetupExcludeItems.propTypes = {
    selectedTemplate: PropTypes.object.isRequired,
    excludedItemIds: PropTypes.array.isRequired,
    setExcludedItemIds: PropTypes.func.isRequired
};

export default SetupExcludeItems;
